import { useContext, useState, type ComponentType, type Key, type MouseEvent, type ReactNode } from "react";
import {
  MoreOutlined,
  LoadingOutlined,
  RollbackOutlined,
  CloseOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import {
  Dropdown,
  Flex,
  Modal,
  notification,
  Table,
  TableProps,
  Tooltip,
  Typography,
} from "antd";
import clsx from "clsx";
import {
  JournalRecordPreview,
  type JournalRecordPreviewMeta,
  isTablePreviewClick,
} from "@/entities/journal/ui";
import { AbilityContext, Can } from "@/shared/lib/casl";
import { queryClient } from "@/shared/lib/react-query";
import { CommentChip, UserBubble, RecordStatusTypes } from "@/shared/ui";
import { NOTIFICATION_MESSAGES, fallbackString, formatDate, formatMoscowTime, getUserFio, useIsUnifiedMode } from "@/shared/utils";
import { journalsQueries, useJournalMutation, DocumentFileRow } from "@entities/journal";
import type { components } from "@entities/journal";
import {
  BasketIcon,
  EditIcon,
  PendingSignIcon,
  RejectedSignIcon,
  StampIcon,
  SuccessfulSignIcon,
  VersionIcon,
} from "@shared/assets/svg/icons";
import { RejectedRecordsCollapsible } from "../../../journals-section-first/ui";
import { getSectionTwoActions } from "../../model/actions-map";
import { RecordHistoryModal } from "../record-history-modal/record-history-modal.tsx";
import { RejectSignModal } from "../reject-sign-modal/reject-sign-modal.tsx";
import {
  sectionSecondSelectionColumnTitle,
  sectionSecondSelectionRenderCell,
} from "../section-second-row-selection-with-can";
import { SignerButtons } from "../signing-button/signer-buttons.tsx";
import "./section-second-table-row.scss";

type SectionTwoListEntry = components["schemas"]["SectionTwoItemForList"];

interface SectionSecondTableProps {
  isSelecting: boolean;
  sectionTwoId?: string;
  sectionTwoListId?: string;
  onRowSelect: (row: string[]) => void;
  onRecordEdit: (recordId: string) => void;
  onRecordView: (recordId: string) => void;
  selectedRows: string[];
  refetch: (options?: RefetchOptions) => Promise<
    QueryObserverResult<
      {
        total?: number;
        section2List?: components["schemas"]["SectionTwoItemForList"][];
        permissions?: components["schemas"]["AclPermissionsInner"][];
      },
      {
        [key: string]: any;
      }
    >
  >;
  records?: {
    total?: number;
    section2List?: components["schemas"]["SectionTwoItemForList"][];
    permissions?: components["schemas"]["AclPermissionsInner"][];
  };
  isLoading: boolean;
}

// type Actions = "sendToSign" | "rejectSign" | "delete" | "versions" | "edit";

// Deprecated: use getSectionTwoActions from model/actions-map instead

const SIGNER_COLUMN_TOOLTIP =
  "Уполномоченный представитель застройщика или технического заказчика, лица, ответственного за эксплуатацию здания, сооружения, и (или) регионального оператора";

const COLUMN_TOOLTIPS = {
  specialJournalName: "Наименование специального журнала",
  dateHandover:
    "Дата передачи журнала застройщику или техническому заказчику",
  journalKeepers:
    "Организация, осуществляющая строительство или организация, осуществляющая подготовку проектной документации, ведущая журнал",
  signer: SIGNER_COLUMN_TOOLTIP,
};

const renderColumnTitle = (label: string, tooltip?: string) =>
  tooltip ? (
    <Tooltip title={tooltip}>
      <span className="journals-section-second-table__column-title">{label}</span>
    </Tooltip>
  ) : (
    label
  );

const renderDatetimeCell = (date?: string | null) => {
  if (!date) {
    return "-";
  }

  const formattedDate = formatDate(date);
  const formattedTime = formatMoscowTime(date);

  if (!formattedDate && !formattedTime) {
    return "-";
  }

  return (
    <div className="journals-section-second-table__datetime">
      {formattedDate ? (
        <span className="journals-section-second-table__datetime-date">
          {formattedDate}
        </span>
      ) : null}
      {formattedTime ? (
        <span className="journals-section-second-table__datetime-time">
          {formattedTime} МСК
        </span>
      ) : null}
    </div>
  );
};

const renderNumberCell = (record: SectionTwoListEntry) => {
  const { sequenceNumber, updateDate } = record;

  return (
    <div className="journals-section-second-table__number">
      <span className="journals-section-second-table__number-value">
        {sequenceNumber ?? "-"}
      </span>
      {updateDate ? (
        <>
          <span className="journals-section-second-table__number-updated">
            изменено {formatDate(updateDate)}
          </span>
          {formatMoscowTime(updateDate) ? (
            <span className="journals-section-second-table__datetime-time">
              {formatMoscowTime(updateDate)} МСК
            </span>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

const getOrganizationsLabel = (count: number) => {
  const mod10 = count % 10;
  const mod100 = count % 100;

  if (mod10 === 1 && mod100 !== 11) {
    return "организация";
  }

  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return "организации";
  }

  return "организаций";
};

type JournalKeeper = NonNullable<
  SectionTwoListEntry["journalKeepersList"]
>[number];

const renderKeeperTooltipContent = (keepers: JournalKeeper[]) => (
  <div>
    {keepers.map((keeper, index) => {
      const representative = keeper.authorizedRepresentativesList?.[0];

      return (
        <div
          key={keeper.company?.id ?? index}
          className="journals-section-second-table__keepers-tooltip-item"
        >
          <div>{keeper.company?.fullName || "-"}</div>
          {representative ? (
            <div>{getUserFio(representative)}</div>
          ) : null}
        </div>
      );
    })}
  </div>
);

const renderJournalKeepersCell = (
  keepers: SectionTwoListEntry["journalKeepersList"],
) => {
  if (!Array.isArray(keepers) || keepers.length === 0) {
    return "-";
  }

  const firstKeeper = keepers[0];
  const firstRepresentative = firstKeeper?.authorizedRepresentativesList?.[0];
  const remainingKeepers = keepers.slice(1);

  return (
    <div className="journals-section-second-table__keepers">
      <div className="journals-section-second-table__keeper-org journals-section-second-table__cell-text">
        {fallbackString(firstKeeper?.company?.fullName)}
      </div>
      {firstRepresentative ? (
        <UserBubble userId={firstRepresentative.userId}>
          <Typography.Text className="journal-table-link">
            {getUserFio(firstRepresentative)}
          </Typography.Text>
        </UserBubble>
      ) : null}
      {firstRepresentative?.position ? (
        <div className="journals-section-second-table__keeper-position journals-section-second-table__cell-text">
          {firstRepresentative.position}
        </div>
      ) : null}
      {remainingKeepers.length > 0 ? (
        <Tooltip title={renderKeeperTooltipContent(remainingKeepers)}>
          <button type="button" className="journals-section-second-table__keepers-more">
            еще {remainingKeepers.length}{" "}
            {getOrganizationsLabel(remainingKeepers.length)}
          </button>
        </Tooltip>
      ) : null}
    </div>
  );
};

export const getSectionSecondTableColumns = (
  optionsCallbacks: {
    onEdit: (itemNoVersionId: string) => void;
    onDelete: (itemNoVersionId: string) => void;
    onReject: (itemNoVersionId: string) => void;
    onSign: (itemNoVersionId: string) => void;
    onGetVersions: (itemNoVersionId: string) => void;
    onRollback: (itemNoVersionId: string) => void;
    onView: (itemNoVersionId: string) => void;
  },
  optionsLoaders: {
    isSigning?: boolean;
  },
  sectionTwoListId: string,
  sectionTwoId: string,
  disableDocumentLinkAction: boolean,
  bulkSendMode = false,
): TableProps<SectionTwoListEntry>["columns"] => {
  const columns: TableProps<SectionTwoListEntry>["columns"] = [
  {
    title: "№",
    dataIndex: "sequenceNumber",
    key: "sequenceNumber",
    width: 88,
    render: (_: unknown, record: SectionTwoListEntry) => renderNumberCell(record),
  },
  {
    title: renderColumnTitle(
      "Наименование специального журнала",
      COLUMN_TOOLTIPS.specialJournalName,
    ),
    key: "specialJournalName",
    width: 220,
    className: "journals-section-second-table__text-cell",
    render: (value: SectionTwoListEntry) => {
      return (
        <Flex vertical gap={4}>
          <Typography.Text className="journals-section-second-table__journal-name journals-section-second-table__cell-text">
            {fallbackString(value.specialJournalName)}
          </Typography.Text>
          {value.journalScanId ? (
            <DocumentFileRow
              inline
              fileName="Цифровая копия журнала"
              fileId={value.journalScanId}
              disableLinkAction={disableDocumentLinkAction}
            />
          ) : null}
        </Flex>
      );
    },
  },
  {
    title: "Дата создания журнала",
    dataIndex: "specialJournalDate",
    key: "specialJournalDate",
    width: 140,
    render: (value?: string) => fallbackString(formatDate(value)),
  },
  {
    title: renderColumnTitle(
      "Дата передачи журнала",
      COLUMN_TOOLTIPS.dateHandover,
    ),
    dataIndex: "dateHandover",
    key: "dateHandover",
    width: 140,
    render: (value?: string) => fallbackString(formatDate(value)),
  },
  {
    title: renderColumnTitle(
      "Организация, уполномоченный сотрудник, ведущий журнал",
      COLUMN_TOOLTIPS.journalKeepers,
    ),
    dataIndex: "journalKeepersList",
    key: "journalKeepersList",
    width: 240,
    className: "journals-section-second-table__text-cell",
    render: (keepers: SectionTwoListEntry["journalKeepersList"]) =>
      renderJournalKeepersCell(keepers),
  },
  {
    title: renderColumnTitle("Подписант", COLUMN_TOOLTIPS.signer),
    dataIndex: "signer",
    key: "signer",
    width: 280,
    render: (
      value: SectionTwoListEntry["signer"],
      record: SectionTwoListEntry,
    ) => {
      const mchdNumber = value?.mchdId;

      let icon;

      switch (record.status) {
        case "AWAITING_SIGNATURE":
          icon = <PendingSignIcon />;
          break;
        case "REJECTED":
          icon = (
            <Flex align="center" gap="8px">
              <RejectedSignIcon />
              {(() => {
                const comments = record?.comments || [];
                const validComments = comments.filter(
                  (comment: any) => comment?.comment && comment.comment.trim(),
                );
                return validComments.length > 0 ? (
                  <Tooltip
                    title={validComments
                      .map((comment: any) => comment.comment)
                      .join("\n")}
                  >
                    <span className="journal-table-comment-trigger">
                      <CommentChip />
                    </span>
                  </Tooltip>
                ) : null;
              })()}
            </Flex>
          );
          break;
        case "SIGNED":
          icon = <SuccessfulSignIcon />;
          break;
        default:
          icon = null;
      }

      return (
        <Flex vertical gap="8px">
          <div>
            <Flex align="center" gap="8px">
              <UserBubble userId={value?.userId}>
                <Typography.Text className="journal-table-link">
                  {value ? getUserFio(value) : null}
                </Typography.Text>
              </UserBubble>
              {icon}
            </Flex>
            {value?.position ? (
              <Typography.Text className="journals-section-second-table__signer-position journals-section-second-table__cell-text">
                {value.position}
              </Typography.Text>
            ) : null}
            {record.status === "SIGNED" && mchdNumber ? (
              <Typography.Text className="journals-section-second-table__cell-text">
                Доверенность № {mchdNumber}
              </Typography.Text>
            ) : null}
          </div>
          {record.status === "AWAITING_SIGNATURE" && !bulkSendMode && (
            <SignerButtons
              sectionTwoId={sectionTwoId}
              itemNoVersionId={record.itemNoVersionId!}
              sectionTwoListId={sectionTwoListId}
              signerUserId={value?.userId}
            />
          )}
        </Flex>
      );
    },
  },
  {
    title: "Дата создания",
    dataIndex: "createDate",
    key: "createDate",
    width: 120,
    render: (_: unknown, record: SectionTwoListEntry) =>
      renderDatetimeCell(record.createDate),
  },
  ...(bulkSendMode
    ? []
    : [
  {
    render: (value: SectionTwoListEntry) => {
      const itemsAdmin: any[] = [];

      itemsAdmin.push({
        key: "view",
        label: "Открыть запись",
        icon: <EyeOutlined />,
        onClick: () => optionsCallbacks?.onView?.(value.itemNoVersionId!),
      });

      if (getSectionTwoActions(value.status).includes("sendToSign")) {
        itemsAdmin.push({
          key: "sendToSign",
          label: "Отправить на подписание",
          icon: optionsLoaders.isSigning ? <LoadingOutlined /> : <StampIcon />,
          onClick: () => optionsCallbacks?.onSign?.(value.itemNoVersionId!),
        });
      }

      if (getSectionTwoActions(value.status).includes("rejectSign")) {
        itemsAdmin.push({
          key: "rejectSign",
          label: "Отозвать с подписания",
          icon: optionsLoaders.isSigning ? <LoadingOutlined /> : <StampIcon />,
          onClick: () => optionsCallbacks?.onReject?.(value.itemNoVersionId!),
        });
      }

      if (getSectionTwoActions(value.status).includes("rollback")) {
        itemsAdmin.push({
          key: "rollback",
          label: "Вернуть в работу",
          icon: <RollbackOutlined />,
          onClick: () => optionsCallbacks?.onRollback?.(value.itemNoVersionId!),
        });
      }

      if (getSectionTwoActions(value.status).includes("edit")) {
        itemsAdmin.push({
          key: "edit",
          label: "Редактировать",
          icon: <EditIcon />,
          onClick: () => optionsCallbacks?.onEdit?.(value.itemNoVersionId!),
        });
      }

      if (getSectionTwoActions(value.status).includes("versions") && value.currentVersionId != null) {
        itemsAdmin.push({
          key: "versions",
          label: "Версии",
          icon: <VersionIcon />,
          onClick: () =>
            optionsCallbacks?.onGetVersions?.(value.itemNoVersionId!),
        });
      }

      const itemsView: any[] = [];
      itemsView.push({
        key: "view",
        label: "Открыть запись",
        icon: <EyeOutlined />,
        onClick: () => optionsCallbacks?.onView?.(value.itemNoVersionId!),
      });
      if (value.currentVersionId != null) {
        itemsView.push({
          key: "versions",
          label: "Версии",
          icon: <VersionIcon />,
          onClick: () =>
            optionsCallbacks?.onGetVersions?.(value.itemNoVersionId!),
        });
      }

      if (getSectionTwoActions(value.status).includes("delete")) {
        itemsAdmin.push({
          key: "delete",
          label: "Удалить",
          icon: <BasketIcon />,
          onClick: () => optionsCallbacks?.onDelete?.(value.itemNoVersionId!),
        });
      }
      return (
        <>
          <Can I="edit" a="section_2">
            <Dropdown
              menu={{
                items: itemsAdmin.filter((column) =>
                  getSectionTwoActions(value.status).includes(
                    column.key as any,
                  ),
                ),
              }}
              trigger={["click"]}
            >
              <div
                className="journal-table-dropdown-trigger"
                onClick={(event) => event.stopPropagation()}
              >
                <MoreOutlined />
              </div>
            </Dropdown>
          </Can>
          <Can not I="edit" a="section_2">
            <>
              <Dropdown
                menu={{
                  items: itemsView,
                }}
                trigger={["click"]}
              >
                <div
                  className="journal-table-dropdown-trigger"
                  onClick={(event) => event.stopPropagation()}
                >
                  <MoreOutlined />
                </div>
              </Dropdown>
            </>
          </Can>
        </>
      );
    },
  },
  ]),
  ];
  return columns;
};

const getColumns = getSectionSecondTableColumns;

export function SectionSecondTable({
  isSelecting,
  sectionTwoId,
  sectionTwoListId,
  onRowSelect,
  onRecordEdit,
  onRecordView,
  selectedRows,
  refetch,
  records,
  isLoading,
}: SectionSecondTableProps) {
  const isUnifiedMode = useIsUnifiedMode();
  const [previewRecord, setPreviewRecord] = useState<JournalRecordPreviewMeta | null>(
    null,
  );
  const { mutate: deleteRecord } = useJournalMutation(
    "delete",
    "/api/v1/journals/section-two/items/{itemNoVersionId}",
  );

  const { mutate: sendToSigning, isPending: isSendingToSigning } =
    useJournalMutation(
      "post",
      "/api/v1/journals/section-two/{sectionTwoListId}/items/{itemNoVersionId}/sendToSignItem",
    );

  const { mutate: rollbackRecord } = useJournalMutation(
    "post",
    "/api/v1/journals/section-two/{sectionTwoListId}/items/{itemNoVersionId}/return",
  );

  const [history, setHistory] = useState<{
    isOpen: boolean;
    itemNoVersionId: null | string;
  }>({
    isOpen: false,
    itemNoVersionId: null,
  });

  const [rejectSign, setRejectSign] = useState<{
    isOpen: boolean;
    itemNoVersionId: null | string;
  }>({
    isOpen: false,
    itemNoVersionId: null,
  });

  const [modal, contextHolder] = Modal.useModal();
  const ability = useContext(AbilityContext);

  const getLeftFooter = (
    OkBtn: ComponentType,
    CancelBtn: ComponentType,
  ): ReactNode => (
    <div className="journal-modal-footer-left">
      <OkBtn />
      <CancelBtn />
    </div>
  );
  const handleDelete = async (itemNoVersionId: string) => {
    const confirmed = await modal.confirm({
      title: "Удаление записи",
      content: "Вы действительно хотите удалить запись?",
      centered: true,
      icon: null,
      okText: "Удалить",
      footer: (_, { OkBtn, CancelBtn }) => getLeftFooter(OkBtn, CancelBtn),
      closable: true,
      closeIcon: <CloseOutlined />,
      maskClosable: true,
    });

    if (!confirmed) {
      return;
    }

    deleteRecord(
      { params: { path: { itemNoVersionId } } },
      {
        onSuccess: () => {
          refetch();
          notification.success({
            message: NOTIFICATION_MESSAGES.ENTRY_DELETED_SUCCESS, // "Запись удалена"
          });
        },
        onError: (error: any) => {
          const status = error?.response?.status || error?.status;
          const description =
            status === 409
              ? NOTIFICATION_MESSAGES.ENTRY_DELETE_RELATED_DATA_ERROR // "Есть связанные данные, удаление невозможно выполнить"
              : NOTIFICATION_MESSAGES.ENTRY_DELETE_NOT_POSSIBLE; // "Не удалось удалить запись"
          notification.error({ message: NOTIFICATION_MESSAGES.ENTRY_DELETE_ERROR_ALT, /* "Ошибка при удалении" */ description });
        },
      },
    );
  };

  const handleGetVersions = (itemNoVersionId: string) =>
    setHistory({ isOpen: true, itemNoVersionId });

  const handleCloseVersions = () =>
    setHistory({ isOpen: false, itemNoVersionId: null });

  const handleSendToSigning = (
    itemNoVersionId: string,
    sectionTwoListId: string,
    sectionTwoId: string,
  ) =>
    sendToSigning(
      {
        params: {
          path: { sectionTwoListId: sectionTwoListId!, itemNoVersionId },
        },
      },
      {
        onSuccess: () => {
          notification.success({
            message: NOTIFICATION_MESSAGES.ENTRY_SENT_TO_SIGNING_ALT, // "Запись отправлена на подписание"
          });
          queryClient.invalidateQueries({
            queryKey:
              journalsQueries.getSectionTwoSignersList(sectionTwoId).queryKey,
          });
          // also invalidate the single-item query so modal re-renders if open
          if (sectionTwoId) {
            queryClient.invalidateQueries(
              {
                queryKey: journalsQueries.getSection2({ sectionTwoId })
                  .queryKey,
              },
              { cancelRefetch: false },
            );
          }
        },
        onError: () =>
          notification.error({
            message: NOTIFICATION_MESSAGES.ERROR, // "Ошибка"
            description: NOTIFICATION_MESSAGES.ENTRY_SEND_TO_SIGNING_ERROR_ALT, // "Не удалось отправить на подписание"
          }),
      },
    );

  const handleRejectSigning = (itemNoVersionId: string) =>
    setRejectSign({ isOpen: true, itemNoVersionId });

  const handleCancelRejectSigning = () =>
    setRejectSign({ isOpen: false, itemNoVersionId: null });

  const handleRollback = async (itemNoVersionId: string) => {
    const confirmed = await modal.confirm({
      title: "Вернуть запись в работу",
      content: "Вы действительно хотите вернуть запись в статус Новый?",
      okText: "Вернуть",
      cancelText: "Отмена",
      centered: true,
      icon: null,
      footer: (_, { OkBtn, CancelBtn }) => getLeftFooter(OkBtn, CancelBtn),
      closable: true,
      closeIcon: <CloseOutlined />,
      maskClosable: true,
    });

    if (!confirmed) {
      return;
    }

    rollbackRecord(
      {
        params: {
          path: { itemNoVersionId, sectionTwoListId: sectionTwoListId! },
        },
      },
      {
        onSuccess: () => {
          if (sectionTwoId) {
            queryClient.invalidateQueries(
              {
                queryKey: journalsQueries.getSection2({ sectionTwoId })
                  .queryKey,
              },
              { cancelRefetch: false },
            );
          }
        },
      },
    );
  };

  const handleRowSelect = (selectedRowKeys: Key[]) => {
    onRowSelect(selectedRowKeys as string[]);
  };

  const rejectedWithComment = (records?.section2List || []).filter(
    (item) => {
      if (item?.status !== "REJECTED") return false;
      const comments = item?.comments || [];
      const validComments = comments.filter(
        (comment: any) => comment?.comment && comment.comment.trim(),
      );
      return validComments.length > 0;
    },
  );

  return (
    <>
      {contextHolder}

      {rejectedWithComment.length > 0 ? (
        <div className="journal-info-block--spaced-bottom">
          <RejectedRecordsCollapsible
            items={rejectedWithComment as any}
            editSubject="section_2"
            onReturnToWork={(item: any) => handleRollback(item?.itemNoVersionId)}
          />
        </div>
      ) : null}

      <AbilityContext.Provider value={ability}>
        <Table<SectionTwoListEntry>
          className="journals-section-second-table"
          rowSelection={{
            type: "checkbox",
            preserveSelectedRowKeys: true,
            getCheckboxProps: (record) => ({
              disabled: record.status !== RecordStatusTypes.NEW,
            }),
            renderCell: (_a, _b, _c, originNode) =>
              isSelecting ? sectionSecondSelectionRenderCell(_a, _b, _c, originNode) : null,
            columnTitle: (node) =>
              isSelecting ? sectionSecondSelectionColumnTitle(node) : null,
            onChange: handleRowSelect,
            selectedRowKeys: selectedRows,
          }}
          columns={getColumns(
          {
            onDelete: handleDelete,
            onGetVersions: handleGetVersions,
            onSign: (itemNoVersionId) =>
              handleSendToSigning(
                itemNoVersionId,
                sectionTwoListId!,
                sectionTwoId!,
              ),
            onEdit: onRecordEdit,
            onReject: handleRejectSigning,
            onRollback: handleRollback,
            onView: onRecordView,
          },
          { isSigning: isSendingToSigning },
          sectionTwoListId!,
          sectionTwoId!,
          isUnifiedMode,
        )}
        rowKey={(record) => record.itemNoVersionId!}
        rowClassName={(record) =>
          clsx(
            "section-second-table-row",
            record?.status === "REJECTED" && "section-row--rejected",
            previewRecord?.itemNoVersionId === record.itemNoVersionId &&
              "journal-section-row--preview-selected",
          )
        }
        onRow={(record) => ({
          onClick: (event: MouseEvent<HTMLElement>) => {
            if (!isTablePreviewClick(event)) return;
            setPreviewRecord({
              itemNoVersionId: record.itemNoVersionId || "",
              sequenceNumber: record.sequenceNumber,
              status: record.status,
              isCurrent: record.isCurrent,
            });
          },
        })}
        dataSource={records?.section2List}
        loading={isLoading}
        pagination={false}
        />
      </AbilityContext.Provider>
      {history.isOpen ? (
        <RecordHistoryModal
          section2ListId={sectionTwoListId!}
          noVersionId={history.itemNoVersionId || ""}
          open={history.isOpen}
          onCancel={handleCloseVersions}
        />
      ) : null}
      <RejectSignModal
        mode="inputter"
        onCancel={handleCancelRejectSigning}
        open={rejectSign.isOpen}
        sectionTwoId={sectionTwoId!}
        sectionTwoListId={sectionTwoListId!}
        itemNoVersionId={rejectSign.itemNoVersionId!}
      />
      <JournalRecordPreview
        sectionNumber={2}
        record={previewRecord}
        onClose={() => setPreviewRecord(null)}
        onOpenRecord={(itemNoVersionId) => {
          onRecordView(itemNoVersionId);
          setPreviewRecord(null);
        }}
      />
    </>
  );
}
